import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Type-to-filter dropdown that also accepts new entries.
//
// Behaves like the free-text input it replaces — whatever is typed becomes the
// value — but adds a filtered list of known options and, when `onCreate` is
// given, an explicit "+ Add" row that saves the typed text to the shared list
// so it is offered again next time.
//
// The menu renders in a portal on document.body rather than next to the input.
// The items table scrolls sideways (`overflow-x: auto`), and that clips its
// children vertically too, which cut the list off after a row and a half. A
// portal escapes the scroll container entirely; the trade-off is that the menu
// must be positioned by hand and follow the input on scroll/resize.
export default function SearchableSelect({
  value = '',
  options = [],
  onChange,
  onCreate,
  placeholder = '',
  createLabel = 'Add',
  className = '',
  inputStyle = {},
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [pos, setPos] = useState(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter(o => o.toLowerCase().includes(q)) : options;
  const canCreate = !!onCreate && !!q && !options.some(o => o.toLowerCase() === q);
  const rowCount = filtered.length + (canCreate ? 1 : 0);

  const close = () => { setOpen(false); setQuery(''); setHighlight(0); setPos(null); };
  const commit = (val) => { onChange(val); close(); };
  const create = () => {
    const v = query.trim();
    if (!v) return;
    onCreate(v);          // persists to the shared list
    commit(v);
  };

  // Anchor the menu to the input, flipping above it when the row sits near the
  // bottom of the window.
  const measure = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const flip = spaceBelow < 200 && r.top > spaceBelow;
    setPos({
      left: r.left,
      width: Math.max(r.width, 210),
      flip,
      top: r.bottom + 2,
      bottom: window.innerHeight - r.top + 2,
      maxHeight: Math.max(120, Math.min(240, (flip ? r.top : spaceBelow) - 12)),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    measure();
    const onMove = () => measure();
    window.addEventListener('scroll', onMove, true); // capture: catches the table's own scrolling
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, measure]);

  // Clicking away keeps whatever was typed, so this never loses a half-typed
  // entry the way a plain <select> would.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      const inField = boxRef.current && boxRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (inField || inMenu) return;
      if (query.trim()) onChange(query.trim());
      close();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, query]); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight(h => Math.min(h + 1, Math.max(rowCount - 1, 0))); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); return; }
    if (e.key === 'Tab') { if (query.trim()) onChange(query.trim()); close(); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight < filtered.length) commit(filtered[highlight]);
      else if (canCreate) create();
      else if (query.trim()) commit(query.trim());
    }
  };

  const row = (label, active, onPick, extra = {}) => (
    <div
      key={extra.isCreate ? '__create' : label}
      onMouseDown={(e) => { e.preventDefault(); onPick(); }}
      onMouseEnter={() => setHighlight(extra.index)}
      style={{
        padding: '0.5rem 0.65rem',
        fontSize: '0.78rem',
        cursor: 'pointer',
        color: extra.isCreate ? 'var(--gold, #F5A800)' : 'var(--cream, #f8f5ef)',
        background: active ? 'rgba(245,168,0,0.14)' : 'transparent',
        borderTop: extra.isCreate ? '1px solid rgba(245,168,0,0.25)' : 'none',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {extra.isCreate ? `+ ${createLabel} "${query.trim()}"` : label}
    </div>
  );

  const menu = open && pos && (filtered.length > 0 || canCreate) ? createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: pos.left,
        ...(pos.flip ? { bottom: pos.bottom } : { top: pos.top }),
        width: pos.width,
        maxHeight: pos.maxHeight,
        overflowY: 'auto',
        zIndex: 9999,
        background: '#141414',
        border: '1px solid rgba(245,168,0,0.3)',
        borderRadius: 'var(--radius)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
      }}
    >
      {filtered.map((o, i) => row(o, i === highlight, () => commit(o), { index: i }))}
      {canCreate && row('create', highlight === filtered.length, create, { index: filtered.length, isCreate: true })}
    </div>,
    document.body
  ) : null;

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={open ? query : value}
        onChange={(e) => { setQuery(e.target.value); setHighlight(0); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(''); setHighlight(0); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        title={value || placeholder}
        className={className}
        style={className
          // styled by the host page (see .qb-in) — only the width is ours
          ? { width: '100%', ...inputStyle }
          : {
            width: '100%', background: 'transparent', border: 'none',
            borderBottom: '1px solid var(--ink-5)', color: 'var(--cream)',
            padding: '0.4rem 0', fontSize: '0.78rem', ...inputStyle,
          }}
      />
      {menu}
    </div>
  );
}
